import axios from '../../../shared/axios/axios';
import { normalizeResource } from '../../../shared/utils/files';

// ncFileManager variable is defined in Web Forms
function getFileManagerRequestParams() {
  return {
    insuredId: window['ncFileManager']['insuredId'],
    policyId: window['ncFileManager']['policyId'],
    noteId: window['ncFileManager']['noteId'],
    taskId: window['ncFileManager']['taskId'],
    agencyId: window['ncFileManager']['agencyId'],
    agentId: window['ncFileManager']['agentId'],
    opportunityId: window['ncFileManager']['opportunityId'],
    ownerType:
      window['ncFileManager']['ownerTypeOverride'] !== undefined
        ? window['ncFileManager']['ownerTypeOverride']
        : window['ncFileManager']['ownerType'],
    currentFullURL: window.location.href,
  };
}

/**
 * hasSignedIn
 *
 * @returns {boolean}
 */
function hasSignedIn() {
  return true;
}

/**
 * Init API
 *
 * @returns {Promise<{apiInitialized: boolean, apiSignedIn: boolean}>}
 */
function init() {
  return {
    apiInitialized: true,
    apiSignedIn: true,
  };
}

async function getCapabilitiesForResource(options, resource) {
  return resource.capabilities || [];
}

async function saveFilesControlConfiguration(controlConfig, isSendFiles) {
  const route = `/files/save-files-control-configuration`;

  const response = await axios.post(route, { config: controlConfig, isSendFiles });

  return response.data;
}

async function copyDefaultFolderStructure() {
  const route = `/files/copy-default-folder-structure`;
  const params = {
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);

  return response.data;
}

async function getFilesInDestinationFolder(options, sourceFileNames, targetItemId, excludeFileIds) {
  return new Promise((resolve, reject) => {
    const route = `/files/by-parent-and-names`;
    const params = {
      fileNames: sourceFileNames,
      parentId: targetItemId,
      excludeFileIds: excludeFileIds ?? [],
    };

    axios
      .post(route, params)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function moveResources(options, sourceItems, targetItemId) {
  const route = `/files/move`;
  const params = {
    sourceItems: sourceItems,
    targetId: targetItemId,
    ...getFileManagerRequestParams(),
  };
  return axios.post(route, params);
}

async function getResourceById(options, id) {
  const route = `/files/byid/`;
  const params = {
    id,
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);

  return normalizeResource(response.data);
}

async function getChildrenForId(options, { id, systemFolderType, sortBy = 'name', sortDirection = 'ASC' }) {
  const route = `/files/children`;

  const params = {
    id,
    systemFolderType,
    orderBy: sortBy,
    orderDirection: sortDirection,
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);

  return response.data.items.map(normalizeResource);
}

async function getParentsForId(options, id) {
  const route = `/files/ancestors-byid/`;
  const params = {
    id,
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);
  return response.data;
}

async function getFolderChildrenForId(id) {
  const route = `/files/folder-children-byid/`;
  const params = {
    id,
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);
  return response.data;
}

async function getBaseResource(options) {
  const route = `/files/root`;
  const params = {
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);

  return normalizeResource(response.data);
}

async function getIdForPartPath(options, currId, systemFolderType, pathArr) {
  const resourceChildren = await getChildrenForId(options, { id: currId, systemFolderType });
  for (let i = 0; i < resourceChildren.length; i++) {
    const resource = resourceChildren[i];
    if (resource.name === pathArr[0]) {
      if (pathArr.length === 1) {
        return resource.id;
      } else {
        return getIdForPartPath(options, resource.id, resource.systemFolderType, pathArr.slice(1));
      }
    }
  }

  return null;
}

async function getIdForPath(options, path) {
  const resource = await getBaseResource(options);
  const pathArr = path.split('/');

  if (pathArr.length === 0 || pathArr.length === 1 || pathArr[0] !== '') {
    return null;
  }

  if (pathArr.length === 2 && pathArr[1] === '') {
    return resource.id;
  }

  return getIdForPartPath(options, resource.id, resource.systemFolderType, pathArr.slice(1));
}

async function getParentIdForResource(options, resource) {
  return resource.parentId;
}

function getResourceName(apiOptions, resource) {
  return resource.name;
}

function getResourceUrl(options, id) {
  const route = `/files/direct-url/${id}`;

  return axios.get(route);
}

async function search(options, formData) {
  const route = `/files/search`;

  const params = {
    ...formData,
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);

  return response.data.items.map((response) => normalizeResource({ ...response, searchString: formData.searchValue }));
}

async function getOneDriveFolderById(id, oneDriveLoginCallback) {
  return new Promise((resolve, reject) => {
    let idRouteParam = '';

    if (id) {
      idRouteParam = `?id=${id}`;
    }

    const route = `/onedrive/folder-by-id${idRouteParam}`;

    axios
      .get(route)
      .then((response) => {
        resolve(normalizeResource(response.data));
      })
      .catch((error) => {
        reject(error);

        if (
          typeof oneDriveLoginCallback === 'function' &&
          error.response &&
          error.response.data &&
          error.response.data.errorCode === 'OneDriveUnauthenticated'
        ) {
          oneDriveLoginCallback();
        }
      });
  });
}

async function getOneDriveCodeRequestUrl() {
  const route = `/onedrive/code-request-url`;

  const response = await axios.get(route);

  return response.data;
}

async function getOneDriveFolderChildren(params, oneDriveLoginCallback) {
  return new Promise((resolve, reject) => {
    const route = `/onedrive/children`;

    axios
      .post(route, params)
      .then((response) => {
        resolve(response.data.items.map(normalizeResource));
      })
      .catch((error) => {
        reject(error);

        if (
          typeof oneDriveLoginCallback === 'function' &&
          error.response &&
          error.response.data &&
          error.response.data.errorCode === 'OneDriveUnauthenticated'
        ) {
          oneDriveLoginCallback();
        }
      });
  });
}

async function assignRootFolderExternalId(externalId) {
  return new Promise((resolve, reject) => {
    const route = `/onedrive/assign-root-folder-externalId`;

    axios
      .post(route, {
        truckingCompanyId: window['ncFileManager']['insuredId'],
        externalId,
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function removeRootFolderExternalId() {
  return new Promise((resolve, reject) => {
    const route = `/onedrive/remove-root-folder-externalId`;

    axios
      .post(route, { truckingCompanyId: window['ncFileManager']['insuredId'] })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function getRootFolderExternalId() {
  return new Promise((resolve, reject) => {
    const route = `/onedrive/root-folder-externalId`;

    axios
      .get(route, {
        params: { truckingCompanyId: window['ncFileManager']['insuredId'] },
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

//#region GoogleDrive integration

async function getGoogleDriveCodeRequestUrl() {
  const route = `/googledrive/code-request-url`;

  const response = await axios.get(route);

  return response.data;
}

async function getGoogleDriveFolderById(id, googleDriveLoginCallback) {
  return new Promise((resolve, reject) => {
    let idRouteParam = '';

    if (id) {
      idRouteParam = `?id=${id}`;
    }

    const route = `/googledrive/folder-by-id${idRouteParam}`;

    axios
      .get(route)
      .then((response) => {
        resolve(normalizeResource(response.data));
      })
      .catch((error) => {
        reject(error);

        if (
          typeof googleDriveLoginCallback === 'function' &&
          error.response &&
          error.response.data &&
          error.response.data.errorCode === 'GoogleDriveUnauthenticated'
        ) {
          googleDriveLoginCallback();
        }
      });
  });
}

async function getGoogleDriveFolderChildren(params, googleDriveLoginCallback) {
  return new Promise((resolve, reject) => {
    const route = `/googledrive/children`;

    axios
      .post(route, params)
      .then((response) => {
        resolve(response.data.items.map(normalizeResource));
      })
      .catch((error) => {
        reject(error);
        if (
          typeof googleDriveLoginCallback === 'function' &&
          error.response &&
          error.response.data &&
          error.response.data.errorCode === 'GoogleDriveUnauthenticated'
        ) {
          googleDriveLoginCallback();
        }
      });
  });
}

async function assignGoogleDriveRootFolderExternalId(externalId) {
  return new Promise((resolve, reject) => {
    const route = `/googledrive/assign-root-folder-externalId`;

    axios
      .post(route, {
        truckingCompanyId: window['ncFileManager']['insuredId'],
        externalId,
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function removeGoogleDriveRootFolderExternalId() {
  return new Promise((resolve, reject) => {
    const route = `/googledrive/remove-root-folder-externalId`;

    axios
      .post(route, { truckingCompanyId: window['ncFileManager']['insuredId'] })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function getGoogleDriveRootFolderExternalId() {
  return new Promise((resolve, reject) => {
    const route = `/googledrive/root-folder-externalId`;

    axios
      .get(route, {
        params: { truckingCompanyId: window['ncFileManager']['insuredId'] },
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

//#endregion GoogleDrive integration

export default {
  init,
  hasSignedIn,
  saveFilesControlConfiguration,
  copyDefaultFolderStructure,
  getIdForPath,
  getResourceById,
  getCapabilitiesForResource,
  getChildrenForId,
  getParentsForId,
  getFolderChildrenForId,
  getParentIdForResource,
  getResourceName,
  getResourceUrl,
  search,
  getOneDriveFolderById,
  getOneDriveCodeRequestUrl,
  getOneDriveFolderChildren,
  assignRootFolderExternalId,
  removeRootFolderExternalId,
  getRootFolderExternalId,
  getGoogleDriveCodeRequestUrl,
  getGoogleDriveFolderById,
  getGoogleDriveFolderChildren,
  assignGoogleDriveRootFolderExternalId,
  removeGoogleDriveRootFolderExternalId,
  getGoogleDriveRootFolderExternalId,
  getFilesInDestinationFolder,
  moveResources,
};
